import { useRef, useState, useEffect } from 'react';
import NextImage, { type ImageProps as NextImageProps } from 'next/image';
import GridContainer from 'components/containers/grid-container';
import NoImage from 'components/helper/no-image';
import styles from './all-spreads-overlay.module.scss';
import type { UUID } from 'node:crypto';
import type { ViewerAction } from '../../types';

export default function AllSpreadsOverlay({
	thumbnails,
	currentPageIndex,
	dispatch,
	hidden = true,
}: AllSpreadsOverlayProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const activeThumbnailRef = useRef<HTMLElement>(null);

	useEffect(() => {
		if (hidden) return;
		containerRef.current?.focus();
		activeThumbnailRef.current?.scrollIntoView({
			behavior: 'instant',
			block: 'nearest',
		});
	}, [hidden]);

	const handleChange = (nextIndex: number) => {
		if (nextIndex !== currentPageIndex) {
			dispatch({ type: 'go_to_page', index: nextIndex });
		}

		dispatch({ type: 'toggle_all_spreads' });
	};
	const handleClose = () => dispatch({ type: 'toggle_all_spreads' });

	return (
		<div
			className={[styles.container, hidden ? styles.hidden : ''].join(' ')}
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					handleClose();
				}
			}}
			tabIndex={-1}
			ref={containerRef}
		>
			<GridContainer columns={4} className={styles.thumbnailsGrid}>
				{thumbnails.map(({ title, url, id }, i) => {
					const isCurrentPage = i === currentPageIndex;

					return (
						<figure
							key={id}
							className={[
								styles.thumbnailItem,
								isCurrentPage ? styles.currentPage : '',
							].join(' ')}
							tabIndex={0}
							role="button"
							onClick={() => handleChange(i)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleChange(i);
								}
							}}
							ref={(node: HTMLElement) => {
								if (!node || !isCurrentPage) return;
								activeThumbnailRef.current = node;
							}}
						>
							<div className={styles.thumbnailImageWrapper}>
								<Image
									src={url}
									alt={`Thumbnail of ${title}${isCurrentPage ? ' (Current)' : ''}`}
									fill={true}
									className={styles.thumbnailImage}
									loading="lazy"
									placeholder="empty"
									sizes="(max-width: 960px) 100vw, 400px"
								/>
								<div
									className={styles.thumbnailHoverOverlay}
									aria-hidden="true"
								>
									<span title={title} className={styles.thumbnailTitle}>
										{title}
									</span>
								</div>
							</div>
							<figcaption
								title={title}
								className={`${styles.thumbnailTitle} ${styles.mobileThumbnailTitle}`}
							>
								{title}
							</figcaption>
						</figure>
					);
				})}
			</GridContainer>
		</div>
	);
}

function Image(props: NextImageProps) {
	const [hasError, setHasError] = useState(false);

	const handleError = () => {
		setHasError(true);
	};

	return hasError ? (
		<NoImage className={styles.noImagePlaceholder} />
	) : (
		<NextImage {...props} onError={handleError} />
	);
}

interface AllSpreadsOverlayProps {
	thumbnails: { title: string; url: string; id: UUID }[];
	currentPageIndex: number;
	dispatch: React.Dispatch<ViewerAction>;
	hidden?: boolean;
}
